import _, { first, forEach, indexOf, isNil, map, orderBy, reduce, uniq } from 'lodash';
import React, { Fragment, FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navbar, NavbarBrand, NavbarText, Table } from 'reactstrap';
import { gql, useQuery } from 'urql';
import { DateTime, LoadingUnitIdentifier } from '.';
import { DangerousGoods, HacpackQuery, HacpackQueryTerminalArguments, TerminalHandlingsArguments, Handling, Terminal } from '../types/graph';
import styled from '../themes/themed-styled-components';
import { GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride } from '../types';
import Loading from './Loading';


type TerminalIspsCodeQueryData = GraphDeepPartialOverride<HacpackQuery, {
    terminal: TerminalIspsCodeTerminal
}>
type TerminalIspsCodeTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'id' | 'name' | 'slug' | 'port', {
    handlings: TerminalIspsCodeTerminalHandling[]
}>

type TerminalIspsCodeTerminalHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'loadingUnitIdentifier' | 'checkInDate' | 'boardingShipFromTerminal' | 'unboardingShipFromTerminal' | 'area' | 'bay' | 'row' | 'tier', {
    dangerousGoods: TerminalIspsCodeTerminalHandlingsDangerousGoods[]
}>

type TerminalIspsCodeTerminalHandlingsDangerousGoods = GraphDeepPartialNotListed<DangerousGoods, 'id' | 'unNumber' | 'packingGroup' | 'weight' | 'numberOfPackages' | 'adrClass' | 'adrSecondaryClasses'>

type TerminalIspsCodeQueryArguments = HacpackQueryTerminalArguments & TerminalHandlingsArguments;

const terminalSegregationQuery = gql<TerminalIspsCodeQueryData, TerminalIspsCodeQueryArguments>`
query terminal($id: Int!, $checkedIn: Boolean) {
    terminal (id: $id) {
        id
        name
        slug
        port
        handlings (checkedIn: $checkedIn) {
            id
            loadingUnitIdentifier
            checkInDate
            boardingShipFromTerminal
            unboardingShipFromTerminal
            area
            bay
            row
            tier
            dangerousGoods {
                id
                unNumber
                packingGroup
                weight
                numberOfPackages
                adrClass
                adrSecondaryClasses
            }
        }
    }
}`;

const StyledTable = styled(Table)`
    margin-bottom: 0;
    thead, tbody {
        td, th {
            text-align: center;
            vertical-align: middle;
        }
    }
    colgroup:nth-child(even) col {
        background-color: rgba(0,0,0,.05);
    }
`;

type IspsCodeData = Pick<Handling, 'id' | 'loadingUnitIdentifier' | 'checkInDate' | 'boardingShipFromTerminal' | 'unboardingShipFromTerminal' | 'area' | 'bay' | 'row' | 'tier'> & {
    reasons: string[]
};

type TerminalIspsCodeProps = {
    id: number
}

type HandlingPosition = {
    area?: string | null,
    bay?: string | null,
    row?: string | null,
    tier?: string | null
}

type PositionConverter = (position: HandlingPosition) => HandlingPosition;

const tierMap:Record<string,string> = {
    '82': 'A',
    '84': 'B',
    '86': 'C',
    '88': 'D',
    '90': 'E',
}

const lorenziniBayMap: Record<string, string> = {
    '002': '01',
    '001': '01A',
    '003': '01B',
    '006': '02',
    '005': '02A',
    '007': '02B'
};

const lorenziniRowMap: Record<string, string> = {
    '001': '02',
    '002': '04',
    '003': '06',
    '004': '08',
    '005': '10'
};

const sechRowMap: Record<string, string> = {
    '001': 'N',
    '002': 'M',
    '003': 'L',
    '004': 'I',
    '005': 'H',
    '006': 'G',
    '007': 'F',
    '008': 'E',
    '009': 'D',
    '010': 'C',
    '011': 'B',
    '012': 'A',
}

const positionConverters: Record<string, PositionConverter> = {
    'SECH': ({ area, row, tier, bay }) => ({
        tier: isNil(tier) ? tier : tierMap[tier] ?? tier,
        row: isNil(row) ? row : sechRowMap[row] ?? row,
        area,
        bay
    }),
    'MEDCENTER': ({ area, row, tier, bay }) => ({
        tier: isNil(tier) ? tier : tierMap[tier] ?? '',
        row: isNil(area) ? area : `${row}${area[0]?.toUpperCase() ?? ''}`,
        area,
        bay
    }),
    'LORENZINI': ({ bay, row, ...position }) => ({
        bay: isNil(bay) ? bay : lorenziniBayMap[bay] ?? bay,
        row: isNil(row) ? row : lorenziniRowMap[row] ?? row,
        ...position
    })
}

const TerminalIspsCode: FunctionComponent<TerminalIspsCodeProps> = ({ id }) => {

    const { t } = useTranslation('common');
    //const { isSuperuser } = useCurrentUser();

    const [result] = useQuery<TerminalIspsCodeQueryData, TerminalIspsCodeQueryArguments>({
        query: terminalSegregationQuery,
        variables: { id, checkedIn: true }
    });

    const { data, /*fetching, error*/ } = result;

    const terminal = data?.terminal;

    const handlings = terminal?.handlings;

    //if () {

    const positionConverter = useMemo(() => positionConverters[terminal?.name ?? ''] ?? null, [terminal]);

    //    return <CenteredContainer>{t('Analisi del rischio in corso')}</CenteredContainer>
    //}
    //res, { dangerousGoods, loadingUnitIdentifier, area, bay, tier, row, boardingShipFromTerminal, unboardingShipFromTerminal, checkInDate }
    const ispsCodeData = useMemo(() => reduce(handlings, (res, handling) => {
        const reasons: string[] = [];

        const {
            id,
            loadingUnitIdentifier,
            boardingShipFromTerminal,
            unboardingShipFromTerminal,
            checkInDate,
            dangerousGoods,
            ...position
        } = handling;

        forEach(dangerousGoods, ({ unNumber, adrClass, adrSecondaryClasses, packingGroup, weight, numberOfPackages }) => {
            forEach([adrClass, ...(adrSecondaryClasses??[])], (cl) => {

                switch (cl?.substr(0, Math.min(3, cl.length))) {
                    case '1.3':
                        if (cl.length < 4 || cl[3] !== 'C') {
                            break;
                        }
                        
                        /* falls through */
                    case '1.1':
                    case '1.2':
                    case '1.5':
                        reasons.push(t('Esplosivo classe 1.1 o 1.2 o 1.3C o 1.5'));
                        break;

                    case '1.4':
                        if (indexOf(['0104', '0237', '0255', '0267', '0289', '0361', '0365', '0366', '0440', '0441', '0455', '0456', '0500'], unNumber) < 0) {
                            reasons.push(t('Esplosivo classe 1.4'))
                        }
                        break;

                    case '2.1':
                        if (weight != null && weight >= 3000 && numberOfPackages === 1) {
                            reasons.push(t('Classe 2.1 - peso ≥ 3000 kg'))
                        }
                        break;

                    case '2.3':
                        reasons.push(t('Classe 2.3'))
                        break;

                    case '3':
                        if ((packingGroup === 'I' || packingGroup === 'II') && weight != null && weight >= 3000 && numberOfPackages === 1) {
                            reasons.push(t('Classe 3 packing group I o II - peso ≥ 3000 kg'))
                        }
                        break;

                    case '4.2':
                        if (packingGroup === 'I' && weight != null && weight >= 3000 && numberOfPackages === 1) {
                            reasons.push(t('Classe 4.2 packing group I - peso ≥ 3000 kg'))
                        }
                        break;

                    case '4.3':
                        if (packingGroup === 'I' && weight != null && weight >= 3000 && numberOfPackages === 1) {
                            reasons.push(t('Classe 4.3 packing group I - peso ≥ 3000 kg'))
                        }
                        break;

                    case '6.1':
                        if (packingGroup === 'I') {
                            reasons.push(t('Classe 6.1 packing group I'))
                        }
                        break;

                    case '8':
                        if (packingGroup === 'I' && weight != null && weight >= 3000 && numberOfPackages === 1) {
                            reasons.push(t('Classe 8 packing group I - peso ≥ 3000 kg'))
                        }
                        break;
                }

            });


            switch (unNumber) {
                case '3379':
                case '3380':
                    reasons.push("Liquido o solido esplosivo desensibilizzato");
                    break;

                case '2814':
                case '2900':
                    reasons.push("Sostanze infettive (solo se di categoria A)");
                    break;

                case '3098':
                case '3099':
                case '3139':
                    if (packingGroup !== 'I') {
                        break;
                    }
                    // falls through
                case '1510':
                case '1745':
                case '1746':
                case '1873':
                case '2015':
                case '2495':
                    if (weight != null && weight >= 3000 && numberOfPackages === 1) {
                        reasons.push(t('Liquido ossidante packing group I - peso ≥ 3000 kg'))
                    }
                    break;

                case '1442':
                case '1447':
                case '1455':
                case '1470':
                case '1475':
                case '1481':
                case '1489':
                case '1502':
                case '1508':
                case '3211':
                case '3408':
                    if (weight != null && weight >= 3000 && numberOfPackages === 1) {
                        reasons.push(t('Perclorati - peso ≥ 3000 kg'))
                    }

                    break;


                case '1942':
                case '2067':
                case '3375':
                case '2426':
                    if (weight != null && weight >= 3000 && numberOfPackages === 1) {
                        reasons.push(t('Ammonio nitrato - peso ≥ 3000 Kg'))
                    }

                    break;
            }
        });

        if (reasons.length === 0) {
            return res;
        }

        const { area, bay, tier, row } = positionConverter?.(position) ?? position;

        res.push({
            id,
            loadingUnitIdentifier,
            area,
            bay,
            tier,
            row,
            boardingShipFromTerminal,
            unboardingShipFromTerminal,
            checkInDate,
            reasons: uniq(reasons)
        });

        return res;
    }, [] as IspsCodeData[]), [handlings, positionConverter, t]);


    if (handlings === undefined) {
        return <Loading />
    }


    return <>
        <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div>
                <NavbarBrand>{t('Codice ISPS')}</NavbarBrand>
                <NavbarText>{t('{{ c }} container totali', { c: handlings?.length ?? 0 })}</NavbarText>
            </div>
            {/*<div className="d-sm-inline-flex flex-sm-row">*/}

            {/*</div>*/}
            {/*<div className="navbar-right d-sm-inline-flex flex-sm-row">*/}

            {/*</div>*/}
        </Navbar>

        <StyledTable className="table-sm">
            <thead>
                <tr>
                    <th>{t('Container')}</th>
                    <th>{t('Area')}</th>
                    <th>{t('Baia')}</th>
                    <th>{t('Riga')}</th>
                    <th>{t('Tiro')}</th>
                    <th>{t('Nave di sbarco')}</th>
                    <th>{t('Nave d\'imbarco')}</th>
                    <th>{t('Data d\'ingresso')}</th>
                    <th>{t('Motivo')}</th>
                </tr>
            </thead>
            <tbody>
                {map(orderBy(ispsCodeData, ['loadingUnitIdentifier', 'asc']), ({
                    id, loadingUnitIdentifier, area, tier, bay, row, unboardingShipFromTerminal,
                    boardingShipFromTerminal, checkInDate, reasons
                }) =>

                    <Fragment key={id}>
                        <tr>
                            <td rowSpan={reasons.length}>
                                <LoadingUnitIdentifier identifier={loadingUnitIdentifier} />
                            </td>
                            <td rowSpan={reasons.length}>{area}</td>
                            <td rowSpan={reasons.length}>{bay}</td>
                            <td rowSpan={reasons.length}>{row}</td>
                            <td rowSpan={reasons.length}>{tier}</td>
                            <td rowSpan={reasons.length}>{unboardingShipFromTerminal ?? '-'}</td>
                            <td rowSpan={reasons.length}>{boardingShipFromTerminal ?? '-'}</td>
                            <td rowSpan={reasons.length}><DateTime date={checkInDate} /></td>
                            <td>{first(reasons)}</td>
                        </tr>

                        {_(reasons).slice(1).map((reason) => <tr><td>{reason}</td></tr>).value()}
                    </Fragment>
                )}
            </tbody>
        </StyledTable>
    </>;
}

export default TerminalIspsCode;